import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import { PERMISSION_GROUPS_URL, useGroupedPermissions } from '../../../../api/permissions';
import { PermissionGroup, GroupedPermission } from '../../../../types/permissions';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import {
  DetailHeader,
  DetailPage,
  DetailRow,
  DetailSection,
} from '../../../../components/ui/detailView';
import { StatusChip } from '../../../../components/ui/statusChip/statusChip.component';

export const TenantPermissionGroupDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading, get } = useCRUD(PERMISSION_GROUPS_URL);
  const { setOverride } = useBreadcrumbs();
  const { query: queryGroupedPermissions, loading: permissionsLoading } = useGroupedPermissions();
  const [group, setGroup] = useState<PermissionGroup | undefined>();
  const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermission[]>([]);
  const [matchedGroups, setMatchedGroups] = useState<GroupedPermission[]>([]);
  const canViewGroups = useHasPermission("auth.view_group");

  const fetchGroup = useCallback(async () => {
    if (!id) return;

    const result = await get(id);

    if (result?.success && result?.data) {
      setGroup(result.data);
      setOverride(`/tenant/permission-groups/${id}`, result.data.name || '');
    } else {
      navigate('/tenant/permission-groups');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get, navigate]);

  useEffect(() => {
    fetchGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('permissionGroupsUpdated', fetchGroup);

    return () => {
      window.removeEventListener('permissionGroupsUpdated', fetchGroup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadGroupedPermissions = async () => {
      const data = await queryGroupedPermissions();
      if (data) {
        setGroupedPermissions(data);
      }
    };
    loadGroupedPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!group || !groupedPermissions.length) {
      setMatchedGroups([]);
      return;
    }

    // Create a set of permission IDs from the group
    const groupPermissionIds = new Set(
      group.permissions?.map((p) => p.id.toString()) || []
    );

    // Find grouped permissions where at least one subgroup has all permissions included in the group
    const matched = groupedPermissions.filter((groupedPerm) => {
      return groupedPerm.subgroups.some((subgroup) => {
        const subgroupPermissionIds = Object.keys(subgroup.permissions);
        return (
          subgroupPermissionIds.length > 0 &&
          subgroupPermissionIds.every((id) => groupPermissionIds.has(id))
        );
      });
    });

    setMatchedGroups(matched);
  }, [group, groupedPermissions]);

  if (!canViewGroups) {
    return null;
  }

  if (loading || permissionsLoading || !group) {
    return <DetailPage loading />;
  }

  const groupPermissionIds = new Set(group.permissions?.map((p) => p.id.toString()) || []);

  return (
    <DetailPage>
      <DetailHeader
        icon={<AdminPanelSettingsOutlinedIcon />}
        title={group.name}
        chips={group.is_service_role && <StatusChip color="info" variant="outlined" label={t('Technician')} />}
      />

      <DetailSection title={t('Basic Information')}>
        <DetailRow label={t('Name')} value={group.name} />
        <DetailRow
          label={t('Members can be assigned to jobs as technicians')}
          value={group.is_service_role ? t('Yes') : t('No')}
        />
      </DetailSection>

      <DetailSection title={t('Permissions')} padded={matchedGroups.length === 0}>
        {matchedGroups.length > 0 ? (
          matchedGroups.map((groupedPerm) => {
            const matchedSubgroups = groupedPerm.subgroups.filter((subgroup) => {
              const subgroupPermissionIds = Object.keys(subgroup.permissions);
              return (
                subgroupPermissionIds.length > 0 &&
                subgroupPermissionIds.every((id) => groupPermissionIds.has(id))
              );
            });

            return (
              <DetailRow key={groupedPerm.id} label={groupedPerm.name}>
                {matchedSubgroups.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {matchedSubgroups.map((subgroup, index) => (
                      <Chip key={index} label={subgroup.name} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </DetailRow>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t('No permissions assigned')}
          </Typography>
        )}
      </DetailSection>
    </DetailPage>
  );
};
